import { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  getDoc,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "../firebase-config";
import { useAppState } from "../appContext";
import { useNotification } from "../components/Notification";
import { query, where, orderBy, limit, startAfter } from "firebase/firestore";

// ---------------------------------------------------------------------------
// sessionStorage cache helpers (TTL = 10 minutes)
// Prevents re-reading all bands on every page navigation
// ---------------------------------------------------------------------------
const SS_TTL_MS = 10 * 60 * 1000; // 10 minutes
const SS_BANDS_KEY = "wt_bands_all";

const ssGet = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > SS_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

const ssSet = (key, data) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // sessionStorage quota exceeded — fail silently
  }
};

const ssDel = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch {}
};

// Custom hook to fetch all bands from Firestore

const bandsCache = {};

const generateBandsCacheKey = (searchTerms, skip, type, take) => {
  const searchKey = searchTerms?.join("-") || "all";
  return `${searchKey}-${skip}-${type}-${take}`;
};

export const useFetchBandsWithSearch = (
  take = 20,
  skip = 0,
  searchTerms = [],
  type = "accordion"
) => {
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [lastDoc, setLastDoc] = useState(null); // Track the last document for pagination
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBands = async () => {
      try {
        setLoading(true);
        const cacheKey = generateBandsCacheKey(searchTerms, skip, type, take);
        // Check if results for this query are already cached
        if (bandsCache[cacheKey]) {
          const cachedResult = bandsCache[cacheKey];
          setBands(cachedResult.bands);
          setTotalItems(cachedResult.totalItems);
          setLastDoc(cachedResult.lastDoc);
          setLoading(false);
          return;
        }

        let bandsQuery = collection(db, "bands");

        if (searchTerms.length > 0) {
          bandsQuery = query(
            bandsQuery,
            where("searchTerms", "array-contains-any", searchTerms)
          );
        }
        const countSnapshot = await getCountFromServer(bandsQuery);

        const totalCount = countSnapshot.data().count;

        bandsQuery = query(bandsQuery, orderBy("bandname", "asc"), limit(take));

        if (skip > 0 && lastDoc) {
          bandsQuery = query(bandsQuery, startAfter(lastDoc));
        }

        const querySnapshot = await getDocs(bandsQuery);
        const fetchedBands = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];

        // Cache the result
        bandsCache[cacheKey] = {
          bands: fetchedBands,
          totalItems: totalCount,
          lastDoc: lastVisible,
        };

        setBands(fetchedBands);
        setTotalItems(totalCount);
        setLastDoc(lastVisible);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching bands:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchBands();
  }, [take, skip, searchTerms, type]);

  return { bands, loading, totalItems, error };
};
let lastCachedBands = null; // Store the last cached data globally

export const useFetchBands = (refresh = null, enableApi = true) => {
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBands = async () => {
      // refresh > 0 means an explicit re-fetch was requested (e.g. after a save).
      // Bust the sessionStorage cache so we get fresh data, but only once.
      if (refresh > 0) {
        ssDel(SS_BANDS_KEY);
      }

      // If enableApi is false, return session/memory cache without hitting Firestore.
      if (!enableApi) {
        const ssData = ssGet(SS_BANDS_KEY);
        if (ssData) {
          setBands(ssData);
        } else if (lastCachedBands) {
          setBands(lastCachedBands);
        }
        setLoading(false);
        return;
      }

      // Check sessionStorage first — survives page navigation within the same browser tab.
      const ssData = ssGet(SS_BANDS_KEY);
      if (ssData) {
        setBands(ssData);
        lastCachedBands = ssData;
        setLoading(false);
        return;
      }

      // Nothing cached — fetch from Firestore.
      setLoading(true);
      try {
        let bandsQuery = collection(db, "bands");
        bandsQuery = query(bandsQuery, orderBy("title_lowercase", "asc"));

        const querySnapshot = await getDocs(bandsQuery);
        const fetchedBands = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Persist to sessionStorage (TTL 10 min) and in-memory fallback.
        ssSet(SS_BANDS_KEY, fetchedBands);
        lastCachedBands = fetchedBands;

        setBands(fetchedBands);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching bands:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchBands();
  }, [refresh, enableApi]);

  return { bands, loading, error };
};

export const useFetchBandById = (bandId) => {
  const [band, setBand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBand = async () => {
      try {
        if (!bandId) return;

        setLoading(true);
        const bandDocRef = doc(db, "bands", bandId);
        const bandDoc = await getDoc(bandDocRef);

        if (bandDoc.exists()) {
          setBand({
            id: bandDoc.id,
            ...bandDoc.data(),
          });
        } else {
          setError("Band not found");
        }
      } catch (err) {
        setError(`Error fetching band: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchBand();
  }, [bandId]);

  return { band, loading, error };
};
// Custom hook to add a new band to Firestore
export const useAddBand = () => {
  const [loading, setLoading] = useState(false);
  const { showSuccessMessage, showWarningMessage } = useNotification();

  const addBand = async (bandData) => {
    try {
      setLoading(true);
      await setDoc(doc(db, "bands", `${bandData.id}`), bandData);
      // Bust the cache so the new band appears immediately in dropdowns
      ssDel(SS_BANDS_KEY);
      showSuccessMessage("Band added successfully");
    } catch (error) {
      showWarningMessage("Error adding band:", error);
    } finally {
      setLoading(false);
    }
  };

  return { addBand, loading };
};

// Custom hook to update an existing band in Firestore
export const useUpdateBand = () => {
  const [loading, setLoading] = useState(false);
  const { showSuccessMessage, showWarningMessage } = useNotification();

  const updateBand = async (bandId, bandData) => {
    try {
      setLoading(true);
      const bandRef = doc(db, "bands", bandId + "");
      await updateDoc(bandRef, bandData);
      // Bust the cache so updated band data is reflected immediately
      ssDel(SS_BANDS_KEY);
      showSuccessMessage("Band updated successfully");
    } catch (error) {
      console.log("Band updated unsuccessfully", error, bandId);
      showWarningMessage("Error updating band:", error);
    } finally {
      setLoading(false);
    }
  };

  return { updateBand, loading };
};

// Custom hook to delete an band from Firestore
export const useDeleteBand = () => {
  const [loading, setLoading] = useState(false);
  const { showSuccessMessage, showWarningMessage } = useNotification();
  const [state, dispatch] = useAppState();

  const deleteBand = async (bandId) => {
    try {
      setLoading(true);
      await deleteDoc(doc(db, "bands", bandId));

      showSuccessMessage("Band Deleted successfully");
    } catch (error) {
      showWarningMessage("Error Deleting Band:", error);
    } finally {
      setLoading(false);
    }
  };

  return { deleteBand, loading };
};
export const useFetchBandByInternalLink = () => {
  const [band, setBand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBand = async (internallink) => {
    if (!internallink) {
      setError("Internal link is required");
      return;
    }

    try {
      setLoading(true);
      const bandsRef = collection(db, "bands");
      const q = query(
        bandsRef,
        where("internallink", "==", `/${internallink.toLowerCase()}/`)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const bandDoc = querySnapshot.docs[0]; // Assuming internallink is unique
        setBand({
          id: bandDoc.id,
          ...bandDoc.data(),
        });
        return { id: bandDoc.id, ...bandDoc.data() };
      } else {
        setError("Band not found");
      }
    } catch (err) {
      setError(`Error fetching band: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return { band, loading, error, fetchBand };
};
